import { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { motion, type Transition } from 'framer-motion';

import type { MarketplaceProperty } from '../types';

export type FloorPlanPhoto = MarketplaceProperty['floorPlanPhotos'][number];

/** How the selected-floor highlight slides between floor cards (settled on with design). */
const FLOOR_SLIDE: Transition = { type: 'spring', visualDuration: 0.2, bounce: 0 };

function floorStatistics(photo: FloorPlanPhoto) {
  const statistics = photo.statistics;
  if (!statistics) return null;
  return [
    `${statistics.bedrooms} bed`,
    `${statistics.bathrooms} bath`,
    statistics.livingSquareFootage == null
      ? null
      : `${statistics.livingSquareFootage.toLocaleString('en-US')} sqft`,
  ]
    .filter(Boolean)
    .join(' · ');
}

export const PdpFloorPlans = ({
  property,
  onOpenPhoto,
}: {
  property: MarketplaceProperty;
  /** Called with the selected plan and the element showing it, so a zoom can grow out of it. */
  onOpenPhoto: (plan: FloorPlanPhoto, origin: HTMLElement) => void;
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const plan = property.floorPlanPhotos[selectedIndex];
  if (!plan) return null;

  return (
    <Col as="section" aria-label="Floorplan" className="gap-6">
      <Text as="h2" className="text-label-lg">
        Floorplan
      </Text>
      {property.floorPlanPhotos.length > 1 ? (
        <Row
          aria-label="Choose a floor plan"
          className="gap-2 overflow-x-auto rounded-xl bg-od-bg-secondary p-2"
        >
          {property.floorPlanPhotos.map((photo, index) => (
            <Button
              key={`${photo.url}-${index}`}
              analyticsName="cosmos-marketplace-pdp-refresh-floor-plan-select"
              aria-label={`Floor ${index + 1}`}
              aria-pressed={selectedIndex === index}
              variant="secondary"
              size="sm"
              onClick={() => setSelectedIndex(index)}
              className={cn(
                'relative isolate h-auto min-h-20 min-w-0 flex-1 flex-col items-start justify-start gap-1 rounded-lg border-0 px-3 py-3 text-left',
                'transition-none not-disabled:active:scale-100',
                'bg-transparent not-disabled:hover:bg-transparent not-disabled:active:bg-transparent',
                selectedIndex !== index && '!text-od-content-secondary',
              )}
            >
              {selectedIndex === index && (
                <motion.span
                  aria-hidden="true"
                  layoutId="pdp-floor-indicator"
                  transition={FLOOR_SLIDE}
                  className="absolute inset-0 -z-10 rounded-lg bg-od-bg-primary"
                />
              )}
              <Text as="span" className="text-body-lg">
                Floor {index + 1}
              </Text>
              {photo.statistics && (
                <Text as="span" className="max-w-full text-body-sm text-od-content-secondary">
                  {floorStatistics(photo)}
                </Text>
              )}
            </Button>
          ))}
        </Row>
      ) : (
        <Col className="gap-1">
          <Text className="text-body-lg">{plan.label || 'Floor plan'}</Text>
          {plan.statistics && (
            <Text className="text-body-sm text-od-content-secondary">{floorStatistics(plan)}</Text>
          )}
        </Col>
      )}
      <Box
        analyticsName="cosmos-marketplace-pdp-refresh-floor-plan-expand"
        aria-label={`Enlarge ${plan.label || 'floor plan'}`}
        onClick={(event) => onOpenPhoto(plan, event.currentTarget)}
        className="w-full overflow-hidden rounded-lg border border-od-border-primary"
      >
        <Image
          src={plan.url}
          alt={plan.label || 'Floor plan'}
          className="max-h-128 w-full object-contain"
          loading="lazy"
        />
      </Box>
    </Col>
  );
};
